import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AuthUser } from '@/types/user'
import { capabilitiesFor, type Capability } from './capabilities'

interface AuthState {
  token: string | null
  user: AuthUser | null

  setSession: (token: string, user: AuthUser) => void
  /**
   * Merge a server-side persona snapshot (from GET /auth/me) into the
   * current cached user. Preserves `phone` from the cache because /auth/me
   * doesn't emit it. No-op when there's no cached user (an /auth/me call
   * without an active session shouldn't create one). See
   * sow_and_reap_backend#80 + PR #84.
   */
  syncPersona: (snapshot: Omit<AuthUser, 'phone'>) => void
  logout: () => void
  hasCapability: (cap: Capability) => boolean
}

/**
 * Persistence key is deliberately distinct from the Flutter app's SharedPreferences
 * keys (Phase 1 §3.4 parallel-running design). Both apps can be logged in
 * simultaneously in different tabs without stomping each other's session.
 */
const STORAGE_KEY = 'varri-console-auth'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,

      setSession: (token, user) => {
        set({ token, user })
      },

      syncPersona: (snapshot) => {
        const current = get().user
        if (!current) return
        set({ user: { ...current, ...snapshot, phone: current.phone } })
      },

      logout: () => {
        set({ token: null, user: null })
      },

      hasCapability: (cap) => {
        const roleId = get().user?.roleId
        return capabilitiesFor(roleId).includes(cap)
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ token: state.token, user: state.user }),
    },
  ),
)
